import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Menu,
  Settings,
} from 'lucide-react';
import UserMenu from '../components/UserMenu';
import NotificationsBell from '../components/NotificationsBell';
import ThemeToggle from '../components/ThemeToggle';
import ContextSwitcher from '../components/ContextSwitcher';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { cn } from '../lib/utils';

/**
 * TopBar — sticky top header rendered inside AppShell.
 *
 * Phase 2: the legacy 4-tab nav (WORKSPACE / PROJECT / SCENE / SHOT) and the
 * inline <FilmSwitcher /> have been replaced by a single, persistent
 * <ContextSwitcher /> that surfaces the current Workspace + Film selection
 * (with optional Scene/Shot chips when a deep URL is open) using dropdowns
 * instead of implicit drill-down tabs.
 */
const TopBar = ({ onOpenMobileSidebar }) => {
  const navigate = useNavigate();

  return (
    <header
      className={cn(
        'sticky top-0 z-30 h-16 flex items-center gap-2 md:gap-4',
        'border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70',
        'px-3 md:px-5'
      )}
      data-testid="appshell-topbar"
    >
      {/* Mobile hamburger */}
      <Button
        variant="ghost"
        size="sm"
        className="md:hidden h-9 w-9 p-0 flex-shrink-0"
        onClick={onOpenMobileSidebar}
        aria-label="Open navigation"
        data-testid="appshell-mobile-menu"
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Wordmark */}
      <Link
        to="/"
        className="flex items-center gap-2 flex-shrink-0"
        data-testid="appshell-wordmark"
      >
        <span className="font-display font-bold text-lg md:text-xl tracking-tight text-primary">
          FrameFlow
        </span>
      </Link>

      {/* Phase 2 ContextSwitcher — persistent top-left selector for
          Workspace / Film (+ Scene / Shot chips when a deep URL is open). */}
      <div className="hidden md:flex items-center min-w-0 ml-2 lg:ml-4 flex-1">
        <ContextSwitcher />
      </div>

      {/* Spacer pushes the right-side utilities to the edge on mobile,
          where the ContextSwitcher is hidden behind the hamburger menu. */}
      <div className="md:hidden flex-1" />

      {/* Notifications (M3.6): a real feed — call sheet confirmations, sent
          reports, team changes, billing — with pending invitations pinned. */}
      <NotificationsBell />

      {/* Settings */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 w-9 p-0 flex-shrink-0 hidden md:inline-flex"
            aria-label="Settings"
            onClick={() => navigate('/profile')}
            data-testid="appshell-settings"
          >
            <Settings className="h-5 w-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Settings</TooltipContent>
      </Tooltip>

      <ThemeToggle />
      <UserMenu />
    </header>
  );
};

export default TopBar;
